import { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'uxd-design-system-components';
import Spinner from 'react-bootstrap/Spinner';
import ActiveValue from '~types/ActiveValue';
import CustomizePageProps from '~types/pages/CustomizePageProps';
import { useLocation } from 'react-router-dom';
import ValueCardFP from '../../../components/ValueCardFP/ValueCardFP';
import getText from '../../../assets/locale/locale';
import LocaleContext from '../../../contexts/LocaleContext';
import './FPCustomizePage.scss';
import StyledModal from '../../../components/StyledModal/StyledModal';
import addDataLayerTrigger from '../../../utils/addDataLayerTrigger/addDataLayerTrigger';
import WriteInModal from '../../../components/WriteInModal/WriteInModal';
import { sortValueList } from '../../../utils/valueStatementsUtil/valueStatementsSortUtilFP';
import ValueRow from '../../../components/ValueList/ValueRow/ValueRow';
import { usePreviousPath } from '../../../contexts/PreviousPathContext';
import { useDataLayerInfo } from '../../../contexts/DataLayerContext';

const FPCustomizePage = (props: CustomizePageProps) => {
  const { dataLayerInfo } = useDataLayerInfo();
  const {
    valueList,
    setInitialValuePair,
    customizePageBtnCallback,
    restart,
    isLoading,
  } = props;
  const locId = useContext(LocaleContext);
  const [before, after] = getText(
    locId,
    'description',
    'fpCustomizePage'
  ).split('least');

  const [isMobile, setIsMobile] = useState(false);
  const [startQuizLabel, setStartQuizLabel] = useState(
    getText(locId, 'disabledBtnDefault', 'fpCustomizePage')
  );
  const [quizDisabled, setQuizDisabled] = useState(true);
  const [localValuesList, setLocalValuesList] = useState<ActiveValue[]>([]);
  const [numValuesToRemove, setNumValuesToRemove] = useState(5);

  const [showAdditionalInfoModal, setShowAdditionalInfoModal] = useState(false);
  const [showCustomPriorityEditModal, setShowCustomPriorityEditModal] =
    useState(false);
  const [editModalCurrentCardValue, setEditModalCurrentCardValue] = useState({
    statementId: -1,
    statementText: '',
  });
  const writeInStatementId = 15;
  const maximumPhoneWidth = 768;

  const SPACE = ' ';
  const ENTER = 'Enter';

  const [infoModalContent, setInfoModalContent] = useState('');

  const handleMobileView = () =>
    setIsMobile(window.innerWidth < maximumPhoneWidth);

  const updatedFaInfo = {
    ...dataLayerInfo,
    page_type: 'customize-page',
  };

  const { setPreviousPath, previousPath } = usePreviousPath();
  const currentLocation = useLocation();
  const pageTitle = getText(locId, 'pageTitle', 'fpCustomizePage');
  const pageReferrer = previousPath
    ? `${window.location.origin}${previousPath}${currentLocation.search}`
    : undefined;
  const pageURL = `${window.location.origin}${currentLocation.pathname}${currentLocation.search}`;
  const virtualPageInfo = {
    pagePath: currentLocation.pathname,
    pageTitle,
    page_referrer: pageReferrer,
    external_referrer:
      window.document.referrer && window.document.referrer.trim() !== ''
        ? window.document.referrer
        : undefined,
    page_url: pageURL,
  };

  useEffect(() => {
    document.title = pageTitle;
    handleMobileView();
    window.removeEventListener('resize', handleMobileView);
    window.addEventListener('resize', handleMobileView);
  }, []);

  useEffect(() => {
    if (valueList.length > 0 && valueList[0]) {
      setPreviousPath(currentLocation.pathname);
      addDataLayerTrigger('dataLayer-initialized', updatedFaInfo);
      addDataLayerTrigger('VirtualPageview', virtualPageInfo);
      let tempValueList;
      if (!restart) {
        tempValueList = sortValueList(
          valueList.map((value: ActiveValue) => {
            return {
              statementId: value.statementId,
              statementRank: value.statementRank,
              statementText: value.statementText,
              learnMoreContent: value.learnMoreContent,
              learnMoreLinks: value.learnMoreLinks,
              isActive: true,
              indexId: value.indexId,
              additionalInfo: value.additionalInfo,
            };
          })
        );
      } else {
        tempValueList = sortValueList(valueList);
        setNumValuesToRemove(0);
      }
      setLocalValuesList(tempValueList);
    }
  }, [valueList]);

  useEffect(() => {
    if (numValuesToRemove > 0) {
      setStartQuizLabel(
        `${getText(
          locId,
          'disabledBtnRemove',
          'fpCustomizePage'
        )} ${numValuesToRemove} ${getText(
          locId,
          'disabledBtnMore',
          'fpCustomizePage'
        )}`
      );
      setQuizDisabled(true);
    } else if (numValuesToRemove < 0) {
      setStartQuizLabel(
        `${getText(locId, 'disabledBtnAdd', 'fpCustomizePage')} ${Math.abs(
          numValuesToRemove
        )} ${getText(locId, 'disabledBtnMore', 'fpCustomizePage')}`
      );
      setQuizDisabled(true);
    } else {
      setStartQuizLabel(getText(locId, 'startQuiz', 'fpCustomizePage'));
      setQuizDisabled(false);
    }
  }, [numValuesToRemove]);

  useEffect(() => {
    if (localValuesList.length > 0 && localValuesList[0]) {
      const newLocalValuesList = localValuesList.map((value) => {
        if (value.statementId === editModalCurrentCardValue.statementId) {
          return {
            ...value,
            statementText: editModalCurrentCardValue.statementText,
          };
        }
        return value;
      });
      setLocalValuesList(newLocalValuesList);
    }
  }, [editModalCurrentCardValue]);

  function handleCardClick(valueId: number) {
    setLocalValuesList(
      localValuesList.map((value) => {
        if (value.statementId === valueId) {
          // If currently active, reduce numsValueToRemove since it will be switched to disabled
          setNumValuesToRemove(
            value.isActive ? numValuesToRemove - 1 : numValuesToRemove + 1
          );
          return {
            statementId: value.statementId,
            statementRank: value.statementRank,
            statementText: value.statementText,
            learnMoreContent: value.learnMoreContent,
            learnMoreLinks: value.learnMoreLinks,
            isActive: !value.isActive,
            indexId: value.indexId,
            additionalInfo: value.additionalInfo,
          };
        }
        return value;
      })
    );
  }

  function finalizeValueSelections() {
    let indexNum = 0;
    const finalValuesList = Array.from(localValuesList);

    finalValuesList.forEach((value, index) => {
      if (value.isActive) {
        finalValuesList[index].indexId = indexNum;
        indexNum += 1;
      } else {
        finalValuesList[index].indexId = -1;
      }
    });

    setInitialValuePair(finalValuesList);
    return finalValuesList;
  }

  const mapValuesToCustomizeCards = () => {
    return localValuesList.map((value) => {
      return value ? (
        <div
          className="CustomizePageFP_card"
          key={value.statementId}
          role="button"
          onClick={() => handleCardClick(value.statementId)}
          onKeyDown={(e) => {
            if (e.key === ENTER || e.key === SPACE) {
              e.preventDefault();
              handleCardClick(value.statementId);
            }
          }}
          tabIndex={0}
          aria-pressed={value.isActive}
          data-testid={`valueCardContainerFP-${value.statementId}`}
        >
          <ValueCardFP
            valueStatement={value.statementText}
            valueStatementId={value.statementId}
            disabled={!value.isActive}
            setShowAdditionalInfoModal={setShowAdditionalInfoModal}
            setShowCustomPriorityEditModal={setShowCustomPriorityEditModal}
            isEditable={value.statementId === writeInStatementId}
            setEditModalCurrentCardValue={setEditModalCurrentCardValue}
            infoModalContent={value.additionalInfo}
            setInfoModalContent={setInfoModalContent}
          />
        </div>
      ) : null;
    });
  };

  const mapValuesToCustomizeRows = () => {
    return localValuesList.map((value) => {
      return value ? (
        <div
          className="CustomizePageFP_row"
          key={value.statementId}
          role="button"
          onClick={(e) => {
            e.preventDefault();
            handleCardClick(value.statementId);
          }}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === ENTER || e.key === SPACE) {
              e.preventDefault();
              handleCardClick(value.statementId);
            }
          }}
          tabIndex={0}
        >
          <ValueRow
            valueStatement={value.statementText}
            valueStatementId={value.statementId}
            disabled={!value.isActive}
            setShowAdditionalInfoModal={setShowAdditionalInfoModal}
            setShowCustomPriorityEditModal={setShowCustomPriorityEditModal}
            isEditable={value.statementId === writeInStatementId}
            setEditModalCurrentCardValue={setEditModalCurrentCardValue}
            infoModalContent={value.additionalInfo}
            setInfoModalContent={setInfoModalContent}
          />
        </div>
      ) : null;
    });
  };

  const doesValuesListContainInvalidWriteInValue = () => {
    const writeInPriority = localValuesList.find(
      (value) => value.statementId === writeInStatementId
    );
    return (
      writeInPriority &&
      writeInPriority.isActive &&
      (!writeInPriority.statementText ||
        writeInPriority.statementText.toLowerCase() ===
          getText(locId, 'writeInPlaceHolderText', 'fpCustomizePage'))
    );
  };

  const handleStartBtnClick = () => {
    if (!doesValuesListContainInvalidWriteInValue()) {
      addDataLayerTrigger('get-started-customize-page', {});
      window.removeEventListener('resize', handleMobileView);
      customizePageBtnCallback(finalizeValueSelections());
    } else {
      const writeInPriority = localValuesList.find(
        (value) => value.statementId === writeInStatementId
      );
      setEditModalCurrentCardValue({
        statementId: writeInPriority.statementId,
        statementText: writeInPriority.statementText,
      });
      setShowCustomPriorityEditModal(true);
    }
  };

  return (
    <div className="CustomizePageFP" data-testid="customize-pageFP">
      <div className="CustomizePageFP_content">
        <div className="CustomizePageFP_intro">
          <h1 id="heading-no-focus" className="heading--semi-bold">
            {getText(locId, 'header', 'fpCustomizePage')}
          </h1>
          <div className="CustomizePageFP_headerDesc">
            <p data-testid="customize-headerDesc-fp">
              {before}
              <strong>least</strong>
              {after}
            </p>
          </div>
        </div>
        <div className="CustomizePageFP_btnContainer">
          <span
            aria-live="polite"
            aria-relevant="text"
            aria-atomic="true"
            className="CustomizePageFP_visually-hidden"
          >
            {startQuizLabel}
          </span>
          <Button
            label={startQuizLabel}
            disabled={quizDisabled}
            onClick={() => handleStartBtnClick()}
            attributes={{
              value: '',
              id: 'get-started-buttonFP',
            }}
          />
        </div>
        {isMobile ? (
          <>
            <div className="CustomizePageFP_rowsListContainer">
              {isLoading ? (
                <div id="spinner" data-testid="customize-spinnerFP">
                  <Spinner animation="border" />
                </div>
              ) : (
                mapValuesToCustomizeRows()
              )}
            </div>
            <div className="CustomizePageFP_btnContainer">
              <Button
                label={startQuizLabel}
                disabled={quizDisabled}
                onClick={() => handleStartBtnClick()}
                attributes={{
                  value: '',
                  id: 'get-started-buttonFP',
                }}
              />
            </div>
          </>
        ) : (
          <div className="CustomizePageFP_cardsContainer">
            {isLoading ? (
              <div id="spinner" data-testid="customize-spinnerFP">
                <Spinner animation="border" />
              </div>
            ) : (
              mapValuesToCustomizeCards()
            )}
          </div>
        )}
        <StyledModal
          displayModal={showAdditionalInfoModal}
          closeModalHandler={() => setShowAdditionalInfoModal(false)}
          headerText={infoModalContent}
        />
        <WriteInModal
          currentCardValue={editModalCurrentCardValue}
          setCurrentCardValue={setEditModalCurrentCardValue}
          showModal={showCustomPriorityEditModal}
          closeModalHandler={() => setShowCustomPriorityEditModal(false)}
        />
      </div>
      <div className="footerFP">
        <div className="disclaimerFP">
          <p className="microcopy--2">
            {getText(locId, 'customizePageDisclosure', 'fpCustomizePage')}
          </p>
        </div>
      </div>
    </div>
  );
};

FPCustomizePage.propTypes = {
  valueList: PropTypes.arrayOf(
    PropTypes.shape({
      statementId: PropTypes.number,
      statementText: PropTypes.string,
      statementRank: PropTypes.number,
      isActive: PropTypes.bool,
      indexId: PropTypes.number,
    })
  ).isRequired,
  setInitialValuePair: PropTypes.func.isRequired,
  customizePageBtnCallback: PropTypes.func.isRequired,
  restart: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

export default FPCustomizePage;
